import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaCrown } from "react-icons/fa";
import { getSubscriptionStatus } from "../services/subscriptionService";

// The broadcaster plan's live limits (GET /subscription/status); null until loaded or when it fails
export const useSubscriptionStatus = () => {
  const [status, setStatus] = useState(null);

  const refresh = useCallback(
    () =>
      getSubscriptionStatus()
        .then(setStatus)
        .catch(() => setStatus(null)),
    []
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { status, refresh };
};

// Why the host's plan stops a live from starting, or null. Without a status the API still enforces the plan.
export const PRIVATE_LIVE_UPGRADE_REASON = "Private lives (followers or friends only) are not included in your plan.";

export const goLiveBlockReason = (status, { donations = false, privateAudience = false } = {}) => {
  if (!status) return null;
  if (!status.can_go_live) return "Your plan is not active. Choose a plan to go live.";
  if (!status.can_start_new_session) {
    return `You have used all ${status.sessions_used} lives in your plan for this billing period.`;
  }
  if (donations && !status.donations_enabled) return "Donation lives are not included in your plan.";
  if (privateAudience && !status.private_live_enabled) return PRIVATE_LIVE_UPGRADE_REASON;
  return null;
};

export const PlanLimitsBar = ({ status }) => {
  if (!status) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-[#EBF8ED] px-4 py-2 text-xs text-[#095228]">
      <span className="font-semibold">{status.plan_name}</span>
      <span>{status.sessions_remaining < 0 ? "Unlimited lives" : `${status.sessions_remaining} lives left`}</span>
      <span>Up to {status.session_duration_minutes} min</span>
      <span>{status.max_viewers ? `Up to ${status.max_viewers} viewers` : "Unlimited viewers"}</span>
      <Link to="/subscription?tab=broadcaster" className="ml-auto font-semibold underline">
        Upgrade
      </Link>
    </div>
  );
};

export const UpgradePlanModal = ({ reason, onClose }) => {
  const navigate = useNavigate();
  if (!reason) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF8ED] text-[#095228]">
          <FaCrown className="text-2xl" />
        </div>
        <h3 className="mt-4 text-lg font-bold text-gray-900">Upgrade your plan</h3>
        <p className="mt-2 text-sm text-gray-600">{reason}</p>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => navigate("/subscription?tab=broadcaster")}
            className="flex-1 rounded-full bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] px-4 py-2.5 text-sm font-semibold text-white"
          >
            View Plans
          </button>
        </div>
      </div>
    </div>
  );
};
